import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useDirection } from '../../../contexts/DirectionContext';

export interface DropdownItem {
  id: string;
  label: string;
  icon?: string;
  disabled?: boolean;
  selected?: boolean;
  onClick?: () => void;
}

interface DropdownProps {
  trigger: React.ReactNode | ((isOpen: boolean) => React.ReactNode);
  items: DropdownItem[];
  align?: 'left' | 'right';
  width?: 'auto' | 'sm' | 'md' | 'lg';
  className?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  items,
  align = 'left',
  width = 'auto',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isRTL } = useDirection();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const widthClasses = {
    auto: 'w-auto min-w-[200px]',
    sm: 'w-48',
    md: 'w-64',
    lg: 'w-80',
  };

  const alignmentClasses = {
    left: isRTL ? 'right-0' : 'left-0',
    right: isRTL ? 'left-0' : 'right-0',
  };

  return (
    <div ref={dropdownRef} className={`relative h-full ${className}`}>
      <div className="inline-flex h-full" onClick={() => setIsOpen(!isOpen)}>
        {typeof trigger === 'function' ? trigger(isOpen) : trigger}
      </div>

      {isOpen && (
        <div
          className={`
            absolute top-full mt-1
            ${alignmentClasses[align]}
            ${widthClasses[width]}
            bg-background-primary
            border border-border-primary
            rounded-md
            shadow-lg
            z-50
            overflow-hidden
            transition-colors duration-200
          `}
        >
          <div className="py-1">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  item.onClick?.();
                  setIsOpen(false);
                }}
                disabled={item.disabled}
                className={`
                  w-full px-4 py-2
                  flex items-center gap-3
                  text-sm text-left
                  transition-colors duration-200
                  ${item.disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : item.selected
                      ? 'text-primary-600 font-medium hover:bg-surface-secondary'
                      : 'text-text-primary hover:bg-surface-primary'
                  }
                `}
              >
                {item.icon && (
                  <Icon
                    icon={item.icon}
                    className={`w-5 h-5 flex-shrink-0 ${item.selected ? 'text-primary-600' : 'text-text-secondary'}`}
                  />
                )}
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown; 